/**
 * SelfHostedService
 *
 * Service class for interacting with self-hosted Happy relay server.
 * Provides a clean API for:
 * - Socket.IO connection management
 * - Authentication (pairing flow)
 * - Session operations (create, send input, terminate)
 * - Token refresh
 *
 * This service wraps the socket functionality and provides
 * a Promise-based API for async operations.
 */

import { io, Socket } from 'socket.io-client';

/**
 * Socket connection options
 */
const SOCKET_OPTIONS = {
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  transports: ['websocket', 'polling'],
  path: '/socket.io/',
};

/**
 * Pairing code response
 */
export interface PairingCodeResponse {
  code: string;
  expiresIn: number;
}

/**
 * Token response from authentication
 */
export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * Session info response
 */
export interface SessionInfo {
  sessionId: string;
  name: string;
  status: 'active' | 'idle' | 'terminated';
  createdAt: Date;
  lastActivityAt: Date;
}

/**
 * Event callback types
 */
export interface SelfHostedServiceEvents {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: string) => void;
  onAuthRequired?: () => void;
  onSessionOutput?: (sessionId: string, output: string) => void;
  onSessionState?: (sessionId: string, state: string) => void;
  onPairingCode?: (code: string, expiresIn: number) => void;
  onPairingSuccess?: (tokens: TokenResponse) => void;
}

/**
 * SelfHostedService class
 *
 * Manages connection and operations with self-hosted Happy relay server.
 *
 * @example
 * ```ts
 * const service = new SelfHostedService();
 *
 * // Connect
 * await service.connect('https://happy.example.com');
 *
 * // Authenticate
 * const pairing = await service.requestPairingCode();
 * console.log('Enter code:', pairing.code);
 *
 * // Create session
 * const sessionId = await service.createSession();
 *
 * // Send input
 * await service.sendInput(sessionId, 'Hello, Claude!');
 *
 * // Cleanup
 * service.disconnect();
 * ```
 */
export class SelfHostedService {
  private socket: Socket | null = null;
  private relayUrl: string = '';
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiresAt: number = 0;
  private eventHandlers: SelfHostedServiceEvents = {};

  /**
   * Check if connected to relay server
   */
  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Check if authenticated
   */
  get isAuthenticated(): boolean {
    return this.accessToken !== null && Date.now() < this.tokenExpiresAt;
  }

  /**
   * Set event handlers
   */
  setEventHandlers(handlers: SelfHostedServiceEvents): void {
    this.eventHandlers = handlers;
  }

  /**
   * Connect to relay server
   */
  async connect(relayUrl: string): Promise<void> {
    // Disconnect existing socket if any
    if (this.socket) {
      this.socket.disconnect();
    }

    this.relayUrl = relayUrl.replace(/\/$/, '');

    return new Promise((resolve, reject) => {
      console.log(`[SelfHostedService] Connecting to ${this.relayUrl}...`);

      // Build auth options
      const auth: Record<string, string> = { clientType: 'web-ui' };
      if (this.accessToken) {
        auth.token = this.accessToken;
      }

      // Create socket connection
      this.socket = io(this.relayUrl, {
        ...SOCKET_OPTIONS,
        auth,
      });

      // Connection handlers
      this.socket.on('connect', () => {
        console.log('[SelfHostedService] Connected');
        this.eventHandlers.onConnect?.();
        resolve();
      });

      this.socket.on('disconnect', () => {
        console.log('[SelfHostedService] Disconnected');
        this.eventHandlers.onDisconnect?.();
      });

      this.socket.on('connect_error', (error) => {
        console.error('[SelfHostedService] Connection error:', error.message);
        this.eventHandlers.onError?.(error.message);
        reject(new Error(error.message));
      });

      // Auth handlers
      this.socket.on('auth:required', () => {
        console.log('[SelfHostedService] Authentication required');
        this.eventHandlers.onAuthRequired?.();
      });

      this.socket.on('auth:error', (data: { error: string }) => {
        console.error('[SelfHostedService] Auth error:', data.error);
        this.eventHandlers.onError?.(data.error);
      });

      // Session handlers
      // Note: The daemon sends 'content' but legacy clients may send 'output'
      this.socket.on('session:output', (data: { sessionId: string; content?: string; output?: string }) => {
        const outputText = data.content || data.output || '';
        this.eventHandlers.onSessionOutput?.(data.sessionId, outputText);
      });

      this.socket.on('session:state', (data: { sessionId: string; state: string }) => {
        this.eventHandlers.onSessionState?.(data.sessionId, data.state);
      });

      // Pairing handlers
      this.socket.on('pairing:code', (data: { code: string; expiresIn: number }) => {
        console.log('[SelfHostedService] Received pairing code');
        this.eventHandlers.onPairingCode?.(data.code, data.expiresIn);
      });

      this.socket.on('pairing:success', (data: TokenResponse) => {
        console.log('[SelfHostedService] Pairing successful');
        this.setTokens(data.accessToken, data.refreshToken, data.expiresIn);
        this.eventHandlers.onPairingSuccess?.(data);
      });

      // Timeout
      setTimeout(() => {
        if (!this.socket?.connected) {
          reject(new Error('Connection timeout'));
        }
      }, SOCKET_OPTIONS.timeout);
    });
  }

  /**
   * Authenticate with stored token
   */
  async authenticate(token: string): Promise<void> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log('[SelfHostedService] Authenticating with token...');

      this.socket!.emit('auth:token', { token }, (response: { success?: boolean; error?: string; expiresIn?: number }) => {
        if (response.success) {
          console.log('[SelfHostedService] Authentication successful');
          this.accessToken = token;
          if (response.expiresIn) {
            this.tokenExpiresAt = Date.now() + response.expiresIn * 1000;
          }
          resolve();
        } else {
          console.error('[SelfHostedService] Authentication failed:', response.error);
          reject(new Error(response.error || 'Authentication failed'));
        }
      });

      setTimeout(() => reject(new Error('Authentication timeout')), 10000);
    });
  }

  /**
   * Request a pairing code
   */
  async requestPairingCode(): Promise<PairingCodeResponse> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log('[SelfHostedService] Requesting pairing code...');

      this.socket!.emit('pairing:request', {}, (response: { code?: string; expiresIn?: number; error?: string }) => {
        if (response.code && response.expiresIn) {
          console.log('[SelfHostedService] Pairing code received');
          resolve({ code: response.code, expiresIn: response.expiresIn });
        } else {
          console.error('[SelfHostedService] Failed to get pairing code:', response.error);
          reject(new Error(response.error || 'Failed to get pairing code'));
        }
      });

      setTimeout(() => reject(new Error('Pairing code request timeout')), 10000);
    });
  }

  /**
   * Confirm pairing with a code
   */
  async confirmPairing(code: string): Promise<TokenResponse> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log('[SelfHostedService] Confirming pairing...');

      this.socket!.emit('pairing:confirm', { code }, (response: TokenResponse & { error?: string }) => {
        if (response.accessToken && response.refreshToken) {
          console.log('[SelfHostedService] Pairing confirmed');
          this.setTokens(response.accessToken, response.refreshToken, response.expiresIn);
          resolve(response);
        } else {
          console.error('[SelfHostedService] Pairing confirmation failed:', response.error);
          reject(new Error(response.error || 'Pairing confirmation failed'));
        }
      });

      setTimeout(() => reject(new Error('Pairing confirmation timeout')), 10000);
    });
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(): Promise<string> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log('[SelfHostedService] Refreshing access token...');

      this.socket!.emit('auth:refresh', { refreshToken: this.refreshToken }, (response: { accessToken?: string; expiresIn?: number; error?: string }) => {
        if (response.accessToken) {
          console.log('[SelfHostedService] Token refreshed');
          this.accessToken = response.accessToken;
          if (response.expiresIn) {
            this.tokenExpiresAt = Date.now() + response.expiresIn * 1000;
          }
          resolve(response.accessToken);
        } else {
          console.error('[SelfHostedService] Token refresh failed:', response.error);
          reject(new Error(response.error || 'Token refresh failed'));
        }
      });

      setTimeout(() => reject(new Error('Token refresh timeout')), 10000);
    });
  }

  /**
   * Create a new session
   */
  async createSession(args?: string[]): Promise<string> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log('[SelfHostedService] Creating session...');

      this.socket!.emit('session:create', { args }, (response: { sessionId?: string; error?: string }) => {
        if (response.sessionId) {
          console.log(`[SelfHostedService] Session created: ${response.sessionId}`);
          resolve(response.sessionId);
        } else {
          console.error('[SelfHostedService] Session creation failed:', response.error);
          reject(new Error(response.error || 'Session creation failed'));
        }
      });

      setTimeout(() => reject(new Error('Session creation timeout')), 30000);
    });
  }

  /**
   * Send input to a session
   */
  async sendInput(sessionId: string, input: string): Promise<void> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    console.log(`[SelfHostedService] Sending input to session ${sessionId}`);
    this.socket.emit('session:input', { sessionId, input });
  }

  /**
   * Terminate a session
   */
  async terminateSession(sessionId: string): Promise<void> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log(`[SelfHostedService] Terminating session ${sessionId}...`);

      this.socket!.emit('session:terminate', { sessionId }, (response: { success?: boolean; error?: string }) => {
        if (response.success) {
          console.log(`[SelfHostedService] Session terminated: ${sessionId}`);
          resolve();
        } else {
          console.error('[SelfHostedService] Session termination failed:', response.error);
          reject(new Error(response.error || 'Session termination failed'));
        }
      });

      setTimeout(() => reject(new Error('Session termination timeout')), 10000);
    });
  }

  /**
   * Update session title
   * Emits 'session:title:update' event to relay server
   * @param sessionId - Session ID to update
   * @param title - New title (1-100 chars)
   */
  async updateSessionTitle(sessionId: string, title: string): Promise<void> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log(`[SelfHostedService] Updating session title: ${sessionId}`);

      this.socket!.emit('session:title:update', { sessionId, title }, (response: { success?: boolean; error?: string }) => {
        if (response.success) {
          console.log(`[SelfHostedService] Session title updated: ${sessionId}`);
          resolve();
        } else {
          console.error('[SelfHostedService] Session title update failed:', response.error);
          reject(new Error(response.error || 'Failed to update session title'));
        }
      });

      setTimeout(() => reject(new Error('Session title update timeout')), 10000);
    });
  }

  /**
   * Update session title lock status
   * Emits 'session:lock:update' event to relay server
   * @param sessionId - Session ID to update
   * @param locked - Whether to lock the title (prevent AI auto-rename)
   */
  async updateSessionTitleLock(sessionId: string, locked: boolean): Promise<void> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log(`[SelfHostedService] Updating session title lock: ${sessionId} -> ${locked}`);

      this.socket!.emit('session:lock:update', { sessionId, locked }, (response: { success?: boolean; error?: string }) => {
        if (response.success) {
          console.log(`[SelfHostedService] Session title lock updated: ${sessionId}`);
          resolve();
        } else {
          console.error('[SelfHostedService] Session title lock update failed:', response.error);
          reject(new Error(response.error || 'Failed to update session title lock'));
        }
      });

      setTimeout(() => reject(new Error('Session title lock update timeout')), 10000);
    });
  }

  /**
   * Get list of active sessions
   */
  async getSessions(): Promise<SessionInfo[]> {
    if (!this.socket?.connected) {
      throw new Error('Not connected to relay server');
    }

    return new Promise((resolve, reject) => {
      console.log('[SelfHostedService] Getting sessions...');

      this.socket!.emit('session:list', {}, (response: { sessions?: SessionInfo[]; error?: string }) => {
        if (response.sessions) {
          console.log(`[SelfHostedService] Got ${response.sessions.length} sessions`);
          resolve(response.sessions);
        } else {
          console.error('[SelfHostedService] Failed to get sessions:', response.error);
          reject(new Error(response.error || 'Failed to get sessions'));
        }
      });

      setTimeout(() => reject(new Error('Get sessions timeout')), 10000);
    });
  }

  /**
   * Subscribe to session output events
   */
  onSessionOutput(callback: (sessionId: string, output: string) => void): void {
    this.eventHandlers.onSessionOutput = callback;
  }

  /**
   * Subscribe to session state events
   */
  onSessionState(callback: (sessionId: string, state: string) => void): void {
    this.eventHandlers.onSessionState = callback;
  }

  /**
   * Subscribe to disconnect events
   */
  onDisconnect(callback: () => void): void {
    this.eventHandlers.onDisconnect = callback;
  }

  /**
   * Disconnect from relay server
   */
  disconnect(): void {
    if (this.socket) {
      console.log('[SelfHostedService] Disconnecting...');
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Set tokens internally
   */
  private setTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.tokenExpiresAt = Date.now() + expiresIn * 1000;
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  /**
   * Set tokens from external storage
   */
  setStoredTokens(accessToken: string, refreshToken: string, expiresAt: number): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.tokenExpiresAt = expiresAt;
  }

  /**
   * Set just the access token (for sync from adapter)
   * Sets a reasonable default expiration of 15 minutes
   */
  setAccessToken(token: string): void {
    this.accessToken = token;
    // Default to 15 minute expiration if not specified
    this.tokenExpiresAt = Date.now() + 15 * 60 * 1000;
  }

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiresAt = 0;
  }
}

/**
 * Singleton instance for global use
 */
export const selfHostedService = new SelfHostedService();
